<template>
  <div class="container-detail-page">
    <div class="label">基础信息</div>
    <el-form label-width="120px">
      <el-form-item label="分类" required>
        <el-select v-model="params.classifyId2" placeholder="请选择">
          <el-option
            v-for="item in labelList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          >
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="命名" required>
        <el-input v-model="params.name"></el-input>
      </el-form-item>
      <el-form-item label="封面" required>
        <el-upload
          accept=".png,.jpg,.jpeg"
          :show-file-list="false"
          action=""
          :before-upload="
            (file) => {
              handleUpload(file);
              return false;
            }
          "
        >
          <div v-if="params.coverImage">
            <img
              :src="params.coverImage | imgFormat"
              class="review-img"
              style="height: auto"
            />
            <i
              class="el-icon-close"
              @click="params.coverImage = ''"
              @click.stop=""
            ></i>
          </div>
          <i
            v-else
            class="el-icon-plus avatar-uploader-icon"
            style="font-size: 30px"
          ></i>
        </el-upload>
      </el-form-item>
    </el-form>

    <div class="label">模型信息</div>
    <div class="container-model-layout">
      <el-card shadow="never" class="container-model-card">
        <div slot="header" class="card-title">模型文件上传</div>
        <el-form label-width="110px" class="container-model-form">
          <el-form-item label="模型文件">
            <el-radio-group
              v-model="modelSourceType"
              size="mini"
              class="model-source-switch"
              @change="handleModelSourceTypeChange"
            >
              <el-radio-button label="upload">本地上传到 OSS</el-radio-button>
              <el-radio-button label="url">直接填写 OSS 地址</el-radio-button>
            </el-radio-group>

            <el-upload
              v-if="modelSourceType === 'upload'"
              drag
              action=""
              accept=".glb"
              :show-file-list="false"
              class="container-upload-dropzone"
              :before-upload="handleModelFileUpload"
            >
              <div v-if="!modelFileDisplayName" class="dropzone-empty">
                <i class="el-icon-upload upload-icon"></i>
                <div class="upload-title">拖拽 GLB 文件到这里，或点击选择文件</div>
                <div class="upload-desc">
                  支持 `.glb` 格式，选择后会上传到当前 OSS 存储；超过 10MB 时可先压缩后再上传。
                </div>
              </div>
              <div v-else class="dropzone-filled">
                <i class="el-icon-document file-icon"></i>
                <div class="file-meta">
                  <div class="file-name">{{ modelFileDisplayName }}</div>
                  <div class="file-desc">
                    <span>{{ modelSizeText }}</span>
                    <span>{{ modelFileStatusText }}</span>
                  </div>
                </div>
                <el-tag
                  size="mini"
                  :type="compressing || modelFileUploading ? 'warning' : params.modelFilePath ? 'success' : 'info'"
                >
                  {{
                    compressing
                      ? "压缩中"
                      : modelFileUploading
                        ? "上传中"
                        : params.modelFilePath
                          ? "已上传"
                          : "待上传"
                  }}
                </el-tag>
              </div>
            </el-upload>

            <div v-else class="model-url-panel">
              <el-input
                v-model.trim="modelUrlInput"
                clearable
                placeholder="请输入 OSS 上的 .glb 地址，支持完整 URL 或相对路径"
                @keyup.enter.native="applyModelUrl"
              >
                <template slot="append">
                  <el-button type="primary" @click="applyModelUrl">应用地址</el-button>
                </template>
              </el-input>
              <div class="model-url-tip">
                可直接粘贴 OSS 完整地址，或填写对象路径，例如 `common/demo/container.glb`。
              </div>
            </div>

            <div v-if="modelFileDisplayName || modelUrlInput" class="file-actions">
              <el-button
                v-if="modelSourceType === 'upload'"
                type="text"
                @click="triggerModelFilePicker"
              >
                重新选择
              </el-button>
              <el-button
                v-if="modelSourceType === 'url'"
                type="text"
                @click="applyModelUrl"
              >
                重新应用
              </el-button>
              <el-button type="text" class="danger-text" @click="clearModelFile">
                清空模型
              </el-button>
            </div>

            <div v-if="compressing || modelFileUploading" class="upload-progress">
              <i class="el-icon-loading"></i>
              <span>
                {{
                  compressing
                    ? "模型文件正在压缩，请稍候"
                    : "模型文件正在上传，请稍候"
                }}
              </span>
            </div>
            <el-progress
              v-if="modelFileUploading"
              :percentage="modelUploadProgress"
              :stroke-width="10"
              class="upload-progress-bar"
            >
            </el-progress>

            <div v-if="params.modelFilePath" class="uploaded-path">
              <span class="uploaded-path__label">
                {{ modelSourceType === "url" ? "模型地址" : "上传地址" }}
              </span>
              <span class="uploaded-path__value">{{ params.modelFilePath }}</span>
            </div>

            <div v-if="modelSourceType === 'upload'" class="compression-config">
              <div class="compression-config__label">压缩策略</div>
              <el-radio-group
                v-model="compressionPreset"
                size="mini"
                :disabled="modelFileUploading || compressing"
              >
                <el-radio-button
                  v-for="item in compressionPresetOptions"
                  :key="item.value"
                  :label="item.value"
                >
                  {{ item.label }}
                </el-radio-button>
              </el-radio-group>
              <div class="compression-config__desc">
                {{ currentCompressionPreset.description }}，仅在模型超过 10MB 且选择“压缩后上传”时生效。
              </div>
            </div>
          </el-form-item>

          <el-form-item label="光感调节">
            <div class="appearance-panel">
              <div class="appearance-panel__status">
                <el-tag
                  :type="isDefaultModelAppearance ? 'info' : 'success'"
                  size="mini"
                >
                  {{ isDefaultModelAppearance ? "使用默认光感" : "已烘焙默认光感" }}
                </el-tag>
                <span class="appearance-panel__text">
                  {{
                    params.modelFilePath
                      ? "这里会把当前器皿 GLB 的默认亮度和材质参数直接烘焙进模型文件本身，后续在别的编辑器里打开也能明显看到变化。"
                      : "请先上传 GLB 模型，再打开光感调节。"
                  }}
                </span>
              </div>

              <div class="appearance-panel__actions">
                <el-button
                  type="primary"
                  size="mini"
                  :disabled="!params.modelFilePath || modelFileUploading || compressing || appearanceSaving"
                  @click="openAppearanceEditor"
                >
                  {{ isDefaultModelAppearance ? "打开光感调节" : "重新调整光感" }}
                </el-button>
                <el-button
                  v-if="!isDefaultModelAppearance"
                  size="mini"
                  :disabled="appearanceSaving"
                  @click="resetModelAppearance"
                >
                  恢复默认
                </el-button>
              </div>

              <div v-if="appearanceSaving" class="upload-progress">
                <i class="el-icon-loading"></i>
                <span>{{ appearanceStatusText }}</span>
              </div>
              <el-progress
                v-if="appearanceSaving && appearancePhase === 'uploading'"
                :percentage="appearanceUploadProgress"
                :stroke-width="10"
                class="upload-progress-bar"
              >
              </el-progress>

              <div class="appearance-summary">
                <el-tag
                  v-for="item in modelAppearanceSummary"
                  :key="item.label"
                  size="mini"
                  effect="plain"
                >
                  {{ item.label }} {{ item.value }}
                </el-tag>
              </div>
              <div v-if="appearanceSizeChangeText" class="appearance-size-note">
                {{ appearanceSizeChangeText }}
              </div>
            </div>
          </el-form-item>
        </el-form>
      </el-card>

      <el-card shadow="never" class="container-preview-card">
        <div slot="header" class="card-title">模型交互预览</div>
        <div class="preview-panel">
          <div class="preview-thumb">
            <img
              v-if="params.coverImage"
              :src="params.coverImage | imgFormat"
              alt="器皿缩略图"
              class="preview-thumb__image"
            />
            <div v-else class="preview-thumb__empty">
              <i class="el-icon-box"></i>
              <span>模型缩略预留区</span>
            </div>
          </div>

          <div class="preview-box">
            <div class="preview-box-title">当前摘要</div>
            <div class="preview-value">模型来源：{{ modelSourceLabel }}</div>
            <div class="preview-value">
              文件名称：{{ modelFileDisplayName || "尚未选择模型" }}
            </div>
            <div class="preview-value">轮廓组数：{{ detail.length }}</div>
            <div class="preview-value">压缩策略：{{ currentCompressionPreset.label }}</div>
          </div>

          <div class="preview-note">
            器皿页这边已经接上 GLB 上传、压缩、OSS 地址和光感烘焙能力；三点标定不需要，所以这里不展示标定入口。
          </div>
        </div>
      </el-card>
    </div>

    <div class="model-assets-section">
      <div class="model-assets-header">
        <div class="model-assets-title">轮廓与贴图</div>
        <el-button type="success" size="mini" @click="add">新增一组</el-button>
      </div>
      <div class="model-assets-tip">
        下面这部分继续保留器皿原有的轮廓图和贴图维护方式，方便和已有器皿数据保持兼容。
      </div>

      <el-form label-width="80px">
        <div v-for="(item, index) in detail" :key="index" class="model-item">
          <el-form-item label="轮廓">
            <el-upload
              class="avatar-uploader"
              accept=".png,.jpg,.jpeg"
              :show-file-list="false"
              action=""
              :before-upload="
                (file) => {
                  handlePicSuc(file, item);
                }
              "
            >
              <div v-if="item.pointPic">
                <img
                  :src="
                    item.pointPic
                      | imgFormat(
                        false,
                        'lvshuhualv.oss-cn-hangzhou.aliyuncs.com'
                      )
                  "
                  class="review-img"
                />
                <i
                  class="el-icon-close"
                  @click="item.pointPic = ''"
                  @click.stop=""
                ></i>
              </div>
              <i v-else class="el-icon-plus avatar-uploader-icon"></i>
            </el-upload>
          </el-form-item>
          <el-form-item label="贴图" v-if="item.type !== 'boli'">
            <el-upload
              class="avatar-uploader"
              accept=".png,.jpg,.jpeg"
              :show-file-list="false"
              action=""
              :before-upload="
                (file) => {
                  handleUploadSuc(file, item);
                }
              "
            >
              <div v-if="item.map">
                <img
                  :src="
                    item.map
                      | imgFormat(
                        false,
                        'lvshuhualv.oss-cn-hangzhou.aliyuncs.com'
                      )
                  "
                  class="review-img"
                />
                <i
                  class="el-icon-close"
                  @click="item.map = ''"
                  @click.stop=""
                ></i>
              </div>
              <i v-else class="el-icon-plus avatar-uploader-icon"></i>
            </el-upload>
          </el-form-item>
        </div>
      </el-form>
    </div>

    <div class="lv_flex page-actions">
      <el-button type="primary" @click="save(1)">新增并发布</el-button>
      <el-button type="primary" @click="save(2)">存储待发</el-button>
    </div>

    <glb-appearance-editor
      ref="glbAppearanceEditor"
      @confirm="handleAppearanceConfirm"
    />
    <el-dialog
      title="选择压缩模式"
      :visible.sync="compressionModeDialogVisible"
      width="520px"
      :close-on-click-modal="false"
      @close="cancelCompressionModeDialog"
    >
      <div class="compression-dialog-body">
        <div class="compression-dialog-tip">
          当前模型约
          {{
            pendingCompressionFile
              ? `${(pendingCompressionFile.size / 1024 / 1024).toFixed(2)} MB`
              : "--"
          }}
          ，请选择本次上传要使用的压缩模式。
        </div>
        <el-radio-group
          v-model="compressionPreset"
          class="compression-dialog-options"
        >
          <el-radio
            v-for="item in compressionPresetOptions"
            :key="item.value"
            :label="item.value"
            border
            class="compression-dialog-option"
          >
            <div class="compression-dialog-option__title">{{ item.label }}</div>
            <div class="compression-dialog-option__desc">{{ item.description }}</div>
          </el-radio>
        </el-radio-group>
      </div>
      <div slot="footer">
        <el-button @click="cancelCompressionModeDialog">取消</el-button>
        <el-button type="primary" @click="confirmCompressionModeDialog">
          开始压缩并上传
        </el-button>
      </div>
    </el-dialog>

    <canvas width="6000" height="10000" id="c" style="display: none"></canvas>
  </div>
</template>

<script>
import Vue from "vue";
import GlbAppearanceEditor from "../../components/GlbAppearanceEditor.vue";
import {
  createDefaultGlbColorAdjust,
  isDefaultGlbColorAdjust,
  normalizeGlbColorAdjust,
} from "../../utils/glbAppearance";
import {
  compressGlbFile,
  getGlbCompressionPreset,
  GLB_COMPRESSION_PRESETS,
} from "../../utils/glbCompression";

export default {
  components: {
    GlbAppearanceEditor,
  },
  computed: {
    modelFileDisplayName() {
      return this.modelFileName || this.getModelFileName(this.params.modelFilePath);
    },
    modelFileStatusText() {
      if (this.compressing) {
        return "正在压缩 GLB";
      }
      if (this.modelFileUploading) {
        return "正在上传到 OSS";
      }
      if (this.params.modelFilePath) {
        return this.modelSourceType === "url" ? "地址已应用" : "上传完成";
      }
      if (this.modelFileName) {
        return this.modelSourceType === "url" ? "地址待应用" : "等待重新上传";
      }
      return this.modelSourceType === "url" ? "请填写模型地址" : "请选择文件";
    },
    modelSizeText() {
      if (!this.modelFileSize) {
        return "文件大小待定";
      }
      if (
        this.modelUploadMeta.compressed &&
        this.modelUploadMeta.originalSize &&
        this.modelUploadMeta.compressedSize
      ) {
        const compressedSize = this.modelUploadMeta.compressedSize / 1024 / 1024;
        const originalSize = this.modelUploadMeta.originalSize / 1024 / 1024;
        const savedRatio = Math.max(
          0,
          100 -
            (this.modelUploadMeta.compressedSize /
              this.modelUploadMeta.originalSize) *
              100
        );
        return `压缩后 ${compressedSize.toFixed(2)} MB / 原始 ${originalSize.toFixed(
          2
        )} MB / 减少 ${savedRatio.toFixed(0)}%`;
      }
      const size = this.modelFileSize / 1024 / 1024;
      return `${size.toFixed(2)} MB`;
    },
    modelSourceLabel() {
      return this.modelSourceType === "url" ? "OSS 地址" : "本地上传";
    },
    compressionPresetOptions() {
      return GLB_COMPRESSION_PRESETS;
    },
    currentCompressionPreset() {
      return getGlbCompressionPreset(this.compressionPreset);
    },
    isDefaultModelAppearance() {
      return (
        !this.hasCustomModelAppearance ||
        isDefaultGlbColorAdjust(this.modelColorAdjust)
      );
    },
    modelAppearanceSummary() {
      if (!this.hasCustomModelAppearance) {
        return [];
      }
      const adjust = normalizeGlbColorAdjust(this.modelColorAdjust);
      return [
        { label: "亮度", value: `${Math.round(adjust.brightness)}%` },
        { label: "补光", value: `${Math.round(adjust.emissiveStrength)}%` },
        { label: "补光色", value: adjust.emissiveColor || "-" },
      ];
    },
    appearanceStatusText() {
      return this.appearancePhase === "uploading"
        ? "正在上传烘焙后的器皿 GLB，请稍候"
        : "正在烘焙器皿 GLB 光感，请稍候";
    },
    appearanceSizeChangeText() {
      const meta = this.appearanceBakeMeta;
      if (!meta.uploadSize) {
        return "";
      }
      const toMb = (size) => (size / 1024 / 1024).toFixed(2);
      if (!meta.sourceSize) {
        return meta.compressed && meta.bakedSize && meta.bakedSize !== meta.uploadSize
          ? `当前烘焙后的 GLB 大小约 ${toMb(meta.bakedSize)} MB，压缩上传后约 ${toMb(
              meta.uploadSize
            )} MB。`
          : `当前烘焙后的 GLB 大小约 ${toMb(meta.uploadSize)} MB。`;
      }
      const delta = meta.uploadSize - meta.sourceSize;
      const ratio = meta.sourceSize
        ? Math.abs((delta / meta.sourceSize) * 100)
        : 0;
      const trend =
        delta > 0 ? "增加" : delta < 0 ? "减少" : "基本保持不变";
      const summary = `调光后的最终上传文件约 ${toMb(
        meta.uploadSize
      )} MB，较调光前 ${toMb(meta.sourceSize)} MB ${trend}${
        delta === 0 ? "" : `（约 ${ratio.toFixed(0)}%）`
      }。`;
      if (meta.compressed && meta.bakedSize && meta.bakedSize !== meta.uploadSize) {
        return `${summary} 压缩前烘焙文件约 ${toMb(meta.bakedSize)} MB。`;
      }
      return summary;
    },
  },
  data() {
    return {
      detail: [],
      modelFileUploading: false,
      compressing: false,
      appearanceSaving: false,
      modelUploadProgress: 0,
      appearanceUploadProgress: 0,
      appearancePhase: "idle",
      modelSourceType: "upload",
      modelUrlInput: "",
      compressionModeDialogVisible: false,
      modelFileName: "",
      modelFileSize: 0,
      compressionPreset: "standard",
      originalModelFilePath: "",
      modelColorAdjust: createDefaultGlbColorAdjust(),
      hasCustomModelAppearance: false,
      modelConfigJson: "",
      pendingCompressionFile: null,
      modelUploadMeta: {
        originalSize: 0,
        compressedSize: 0,
        compressed: false,
        compressionPresetLabel: "",
      },
      appearanceBakeMeta: {
        sourceSize: 0,
        bakedSize: 0,
        uploadSize: 0,
        compressed: false,
      },
      params: {
        classifyId2: "",
        name: "",
        coverImage: "",
        basicInfo: "",
        connotation: "",
        collocation: "",
        colorSuggestion: "",
        technicalSuggestion: "",
        allegory: "",
        compositionSuggestion: "",
        modelFilePath: "",
        detail: "",
      },
      labelList: [],
      ossUploadData: {},
    };
  },
  created() {
    this.getLabelList();
    this.getKey();
    this.id = this.$route.query.id || "";
    if (this.id) {
      this.getDetail(this.id);
    }
  },
  methods: {
    getDetail(id) {
      this.$ajax.get("/admin/materialManage/getById?id=" + id).then((res) => {
        const resData = res.data || {};
        for (const key in this.params) {
          this.params[key] = resData[key];
        }
        const parsedModelConfig = this.parseModelConfigJson(
          resData.modelConfigJson || ""
        );
        this.detail = this.parseDetailItems(this.params.detail);
        this.modelUrlInput = this.params.modelFilePath || "";
        this.modelFileName = this.getModelFileName(this.params.modelFilePath);
        this.modelFileSize = 0;
        this.modelUploadMeta = {
          originalSize: 0,
          compressedSize: 0,
          compressed: false,
          compressionPresetLabel: "",
        };
        this.modelUploadProgress = 0;
        this.modelConfigJson = this.normalizeStoredModelConfig(
          resData.modelConfigJson || ""
        );
        this.modelColorAdjust = this.parseModelColorAdjust(parsedModelConfig);
        this.hasCustomModelAppearance =
          this.hasStoredModelAppearance(parsedModelConfig);
        this.originalModelFilePath = this.parseAppearanceSourceModelPath(
          parsedModelConfig,
          this.params.modelFilePath
        );
        this.appearanceUploadProgress = 0;
        this.appearancePhase = "idle";
        this.appearanceBakeMeta = {
          sourceSize: 0,
          bakedSize: 0,
          uploadSize: 0,
          compressed: false,
        };
      });
    },
    getKey() {
      this.$ajax.post("/user/oss/getKey?pathName=cover").then((res) => {
        this.ossUploadData.cover = res.data;
      });

      this.$ajax.post("/user/oss/getKey?pathName=common").then((res) => {
        this.ossUploadData.material = res.data;
      });
    },
    // 图片轮廓上传成功
    handlePicSuc(file, item) {
      this.getUploadPromise(
        {
          file,
        },
        this.ossUploadData.material
      ).then((res) => {
        const canvas = document.getElementById("c");
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, c.width, c.height);
        const threeImg = new Image();
        threeImg.src = Vue.filter("imgFormat")(
          res.addr,
          false,
          "lvshuhualv.oss-cn-hangzhou.aliyuncs.com"
        );
        threeImg.crossOrigin = "Anonymous";
        threeImg.onload = () => {
          ctx.drawImage(threeImg, 0, 0, threeImg.width, threeImg.height);
          const imgData = ctx.getImageData(
            0,
            0,
            threeImg.width,
            threeImg.height
          );
          const point = [];
          for (let y = 0; y < canvas.height; y++) {
            let test = 0;
            for (let x = 0; x < canvas.width; x++) {
              const i = y * canvas.width + x;
              if (imgData.data[4 * i + 3]) {
                test = x;
              }
            }
            if (test) {
              point.push(test);
            }
          }
          item.points = point;
        };
        item.pointPic = res.addr;
      });
    },
    handleUpload(file) {
      this.getUploadPromise(
        {
          file,
        },
        this.ossUploadData.cover
      ).then((res) => {
        this.params.coverImage = res.addr;
      });
    },
    handleModelSourceTypeChange(type) {
      if (type === "url") {
        this.modelUrlInput = this.params.modelFilePath || this.modelUrlInput;
      }
    },
    handleModelFileUpload(file) {
      if (this.modelFileUploading || this.compressing) {
        return false;
      }
      const fileName = file.name || "";
      const lowerName = fileName.toLowerCase();
      const maxSize = 200 * 1024 * 1024;
      const compressThreshold = 10 * 1024 * 1024;
      if (!lowerName.endsWith(".glb")) {
        this.$message.error("当前仅支持选择 .glb 模型文件");
        return false;
      }
      if (file.size > maxSize) {
        this.$message.error("glb 文件大小不能超过 200M");
        return false;
      }
      if (!this.ossUploadData.material) {
        this.$message.error("上传配置未加载完成，请稍后重试");
        return false;
      }
      if (file.size > compressThreshold) {
        this.$confirm(
          `当前模型 ${(file.size / 1024 / 1024).toFixed(
            2
          )} MB，已超过 10MB。是否先选择压缩模式，再压缩后上传？`,
          "大文件处理",
          {
            confirmButtonText: "压缩后上传",
            cancelButtonText: "直接上传原文件",
            distinguishCancelAndClose: true,
            closeOnClickModal: false,
            type: "warning",
          }
        )
          .then(() => this.openCompressionModeDialog(file))
          .catch((action) => {
            if (action === "cancel") {
              this.uploadPreparedModelFile(file, {
                compressed: false,
                originalSize: file.size || 0,
                compressedSize: 0,
              });
            }
          });
        return false;
      }
      this.uploadPreparedModelFile(file, {
        compressed: false,
        originalSize: file.size || 0,
        compressedSize: 0,
      });
      return false;
    },
    openCompressionModeDialog(file) {
      this.pendingCompressionFile = file;
      this.compressionModeDialogVisible = true;
    },
    cancelCompressionModeDialog() {
      this.compressionModeDialogVisible = false;
      this.pendingCompressionFile = null;
    },
    confirmCompressionModeDialog() {
      if (!this.pendingCompressionFile) {
        this.compressionModeDialogVisible = false;
        return;
      }
      const file = this.pendingCompressionFile;
      this.pendingCompressionFile = null;
      this.compressionModeDialogVisible = false;
      this.compressAndUploadModelFile(file);
    },
    async compressAndUploadModelFile(file) {
      try {
        const preset = this.currentCompressionPreset;
        this.compressing = true;
        const compressedFile = await compressGlbFile(file, preset);
        const compressedSize = compressedFile.size || 0;
        if (compressedSize >= file.size) {
          this.compressing = false;
          this.$confirm(
            `压缩后文件大小为 ${(compressedSize / 1024 / 1024).toFixed(
              2
            )} MB，没有比原文件更小。是否直接上传原文件？`,
            "压缩结果提示",
            {
              confirmButtonText: "上传原文件",
              cancelButtonText: "取消",
              distinguishCancelAndClose: true,
              closeOnClickModal: false,
              type: "warning",
            }
          )
            .then(() => {
              this.uploadPreparedModelFile(file, {
                compressed: false,
                originalSize: file.size || 0,
                compressedSize: 0,
              });
            })
            .catch(() => {});
          return;
        }
        this.compressing = false;
        this.uploadPreparedModelFile(compressedFile, {
          compressed: true,
          originalSize: file.size || 0,
          compressedSize,
          compressionPresetLabel: preset.label,
        });
      } catch (error) {
        this.compressing = false;
        const message =
          (error && error.message) ||
          "当前模型暂不支持浏览器内压缩，可直接上传原文件";
        this.$confirm(`${message}，是否直接上传原文件？`, "压缩失败", {
          confirmButtonText: "上传原文件",
          cancelButtonText: "取消",
          distinguishCancelAndClose: true,
          closeOnClickModal: false,
          type: "warning",
        })
          .then(() => {
            this.uploadPreparedModelFile(file, {
              compressed: false,
              originalSize: file.size || 0,
              compressedSize: 0,
            });
          })
          .catch(() => {});
      }
    },
    uploadPreparedModelFile(file, meta = {}) {
      this.modelFileName = file.name || "";
      this.modelFileSize = file.size || 0;
      this.params.modelFilePath = "";
      this.originalModelFilePath = "";
      this.modelUrlInput = "";
      this.modelConfigJson = "";
      this.modelColorAdjust = createDefaultGlbColorAdjust();
      this.hasCustomModelAppearance = false;
      this.modelUploadProgress = 0;
      this.modelUploadMeta = {
        originalSize: meta.originalSize || file.size || 0,
        compressedSize: meta.compressedSize || 0,
        compressed: !!meta.compressed,
        compressionPresetLabel: meta.compressionPresetLabel || "",
      };
      this.modelFileUploading = true;
      this.getUploadPromise(
        {
          file,
          onProgress: ({ percentage }) => {
            this.modelUploadProgress = percentage;
          },
        },
        this.ossUploadData.material
      )
        .then((res) => {
          this.params.modelFilePath = this.formatModelFilePath(res.addr);
          this.originalModelFilePath = this.params.modelFilePath;
          this.modelUrlInput = this.params.modelFilePath;
          this.$message.success(
            this.modelUploadMeta.compressed
              ? `模型已按${
                  this.modelUploadMeta.compressionPresetLabel || "当前策略"
                }压缩并上传成功，可继续调整光感`
              : "模型文件上传成功，可继续调整光感"
          );
        })
        .catch(() => {
          this.params.modelFilePath = "";
          this.modelUrlInput = "";
          this.$message.error("模型文件上传失败，请重试");
        })
        .finally(() => {
          this.modelFileUploading = false;
          this.modelUploadProgress = this.params.modelFilePath ? 100 : 0;
        });
    },
    applyModelUrl() {
      const rawValue = (this.modelUrlInput || "").trim();
      if (!rawValue) {
        this.$message.warning("请先输入 OSS 模型地址");
        return;
      }
      if (!/\.glb(\?.*)?$/i.test(rawValue)) {
        this.$message.error("模型地址必须是 .glb 文件");
        return;
      }
      const formattedPath = this.formatModelFilePath(rawValue);
      const hasChanged = formattedPath !== this.params.modelFilePath;
      this.params.modelFilePath = formattedPath;
      this.originalModelFilePath = formattedPath;
      this.modelFileName = this.getModelFileName(formattedPath);
      this.modelFileSize = 0;
      this.modelUploadMeta = {
        originalSize: 0,
        compressedSize: 0,
        compressed: false,
        compressionPresetLabel: "",
      };
      this.modelUrlInput = formattedPath;
      if (hasChanged) {
        this.modelConfigJson = "";
        this.modelColorAdjust = createDefaultGlbColorAdjust();
        this.hasCustomModelAppearance = false;
      }
      this.$message.success(
        hasChanged ? "模型地址已应用，可继续调整光感" : "模型地址已更新"
      );
    },
    parseModelConfigJson(input) {
      if (!input) {
        return null;
      }
      try {
        return typeof input === "string" ? JSON.parse(input) : input;
      } catch (error) {
        return null;
      }
    },
    normalizeStoredModelConfig(input) {
      const parsed = this.parseModelConfigJson(input);
      return parsed ? JSON.stringify(parsed) : "";
    },
    parseDetailItems(detail) {
      if (!detail) {
        return [];
      }
      try {
        const parsed = typeof detail === "string" ? JSON.parse(detail) : detail;
        if (Array.isArray(parsed)) {
          return parsed;
        }
        if (parsed && Array.isArray(parsed.items)) {
          return parsed.items;
        }
      } catch (error) {}
      return [];
    },
    parseModelColorAdjust(modelConfigData) {
      const source =
        (modelConfigData &&
          ((modelConfigData.modelAppearance &&
            modelConfigData.modelAppearance.colorAdjust) ||
            modelConfigData.colorAdjust)) ||
        {};
      return normalizeGlbColorAdjust(source);
    },
    hasStoredModelAppearance(modelConfigData) {
      if (!modelConfigData) {
        return false;
      }
      return !!(
        (modelConfigData.modelAppearance &&
          modelConfigData.modelAppearance.colorAdjust) ||
        modelConfigData.colorAdjust
      );
    },
    parseAppearanceSourceModelPath(modelConfigData, fallbackPath = "") {
      const sourcePath =
        (modelConfigData &&
          ((modelConfigData.modelAppearance &&
            modelConfigData.modelAppearance.sourceModelFilePath) ||
            modelConfigData.sourceModelFilePath)) ||
        fallbackPath;
      return this.formatModelFilePath(sourcePath || "");
    },
    formatModelFilePath(path) {
      if (!path) {
        return "";
      }
      if (/^https?:\/\//.test(path)) {
        return path;
      }
      return (
        "https://lvshuhualv.oss-cn-hangzhou.aliyuncs.com/" +
        path.replace(/^\/+/, "")
      );
    },
    getModelFileName(path) {
      if (!path) {
        return "";
      }
      const cleanPath = path.split("?")[0];
      return decodeURIComponent(cleanPath.split("/").pop() || "");
    },
    openAppearanceEditor() {
      if (!this.params.modelFilePath) {
        this.$message.warning("请先上传 GLB 文件");
        return;
      }
      this.$refs.glbAppearanceEditor.open({
        resourceKey:
          this.id || this.originalModelFilePath || this.params.modelFilePath,
        resourceName: this.params.name || this.modelFileDisplayName || "器皿GLB",
        modelUrl: this.originalModelFilePath || this.params.modelFilePath,
        sourceFileName: this.getModelFileName(
          this.originalModelFilePath || this.params.modelFilePath
        ),
        modelColorAdjust: this.modelColorAdjust,
      });
    },
    async handleAppearanceConfirm(payload) {
      if (!payload || !payload.bakedFile) {
        this.$message.error("GLB 光感烘焙失败，请重试");
        return;
      }
      if (!this.ossUploadData.material) {
        this.$message.error("模型上传配置未加载完成，请稍后重试");
        return;
      }
      const nextColorAdjust = normalizeGlbColorAdjust(
        payload.colorAdjust || payload.colorAdjustJson
      );
      const sourceModelFilePath =
        this.originalModelFilePath || this.params.modelFilePath;
      const sourceSize =
        this.modelFileSize ||
        this.modelUploadMeta.compressedSize ||
        this.modelUploadMeta.originalSize ||
        0;
      this.appearanceSaving = true;
      this.appearancePhase = "baking";
      this.appearanceUploadProgress = 0;
      try {
        let uploadFile = payload.bakedFile;
        let compressed = false;
        let compressedSize = 0;
        if (uploadFile.size > 10 * 1024 * 1024) {
          try {
            const compressedFile = await compressGlbFile(
              uploadFile,
              this.currentCompressionPreset
            );
            if (compressedFile.size < uploadFile.size) {
              uploadFile = compressedFile;
              compressed = true;
              compressedSize = compressedFile.size || 0;
            }
          } catch (error) {
            compressed = false;
            compressedSize = 0;
          }
        }
        this.appearancePhase = "uploading";
        const res = await this.getUploadPromise(
          {
            file: uploadFile,
            onProgress: ({ percentage }) => {
              this.appearanceUploadProgress = percentage;
            },
          },
          this.ossUploadData.material
        );
        this.modelColorAdjust = nextColorAdjust;
        this.hasCustomModelAppearance = true;
        this.params.modelFilePath = this.formatModelFilePath(res.addr);
        this.originalModelFilePath = this.formatModelFilePath(
          sourceModelFilePath
        );
        this.modelFileName = this.getModelFileName(this.params.modelFilePath);
        this.modelFileSize = uploadFile.size || 0;
        this.modelUrlInput = this.params.modelFilePath;
        this.modelUploadMeta = {
          originalSize: uploadFile.size || 0,
          compressedSize,
          compressed,
          compressionPresetLabel: compressed
            ? this.currentCompressionPreset.label
            : "",
        };
        this.appearanceBakeMeta = {
          sourceSize,
          bakedSize: (payload.bakedFile && payload.bakedFile.size) || 0,
          uploadSize: uploadFile.size || 0,
          compressed,
        };
        this.modelConfigJson = this.buildModelConfigJsonPayload();
        this.$message.success("器皿 GLB 默认光感已烘焙进模型并上传成功");
      } catch (error) {
        console.error("Container GLB appearance upload failed:", error);
        this.$message.error(
          (error && error.message) || "器皿 GLB 光感烘焙上传失败，请重试"
        );
      } finally {
        this.appearanceSaving = false;
        this.appearanceUploadProgress = this.params.modelFilePath ? 100 : 0;
        this.appearancePhase = "idle";
      }
    },
    resetModelAppearance() {
      this.modelColorAdjust = createDefaultGlbColorAdjust();
      this.hasCustomModelAppearance = false;
      if (
        this.originalModelFilePath &&
        this.originalModelFilePath !== this.params.modelFilePath
      ) {
        this.params.modelFilePath = this.originalModelFilePath;
        this.modelUrlInput = this.originalModelFilePath;
        this.modelFileName = this.getModelFileName(this.params.modelFilePath);
      }
      this.modelConfigJson = "";
      this.$message.success("已恢复为默认光感参数");
    },
    buildModelConfigJsonPayload() {
      if (!this.params.modelFilePath) {
        return "";
      }
      if (!this.hasCustomModelAppearance) {
        return "";
      }
      const existingConfig = this.parseModelConfigJson(this.modelConfigJson) || {};
      const colorAdjust = normalizeGlbColorAdjust(this.modelColorAdjust);
      const sourceModelFilePath =
        this.originalModelFilePath || this.params.modelFilePath;
      return JSON.stringify({
        ...existingConfig,
        format: "glb",
        url: this.params.modelFilePath,
        sourceModelFilePath,
        colorAdjust,
        modelAppearance: {
          version: 1,
          bakedIntoModel: true,
          sourceModelFilePath,
          colorAdjust,
        },
      });
    },
    triggerModelFilePicker() {
      const input = this.$el.querySelector(
        ".container-upload-dropzone input[type='file']"
      );
      input && input.click();
    },
    clearModelFile() {
      this.params.modelFilePath = "";
      this.modelUrlInput = "";
      this.modelFileName = "";
      this.modelFileSize = 0;
      this.modelUploadMeta = {
        originalSize: 0,
        compressedSize: 0,
        compressed: false,
        compressionPresetLabel: "",
      };
      this.modelUploadProgress = 0;
      this.originalModelFilePath = "";
      this.modelColorAdjust = createDefaultGlbColorAdjust();
      this.hasCustomModelAppearance = false;
      this.modelConfigJson = "";
      this.appearanceUploadProgress = 0;
      this.appearancePhase = "idle";
      this.appearanceBakeMeta = {
        sourceSize: 0,
        bakedSize: 0,
        uploadSize: 0,
        compressed: false,
      };
      this.$message.success("模型文件已清空");
    },
    // 贴图上传成功
    handleUploadSuc(file, item) {
      this.getUploadPromise(
        {
          file,
        },
        this.ossUploadData.material
      ).then((res) => {
        item.map = res.addr;
      });
    },
    add() {
      this.detail.push({
        map: "",
        pointPic: "",
        points: [],
      });
    },
    getLabelList() {
      this.$ajax.post("/admin/classifyManage/queryAll").then((res) => {
        this.labelList = res.data.find((item) => item.id === 2).child;
      });
    },
    save(status) {
      if (this.modelFileUploading || this.compressing || this.appearanceSaving) {
        this.$message.warning("素材文件处理中，请稍候再提交");
        return;
      }
      this.$ajax
        .post(
          this.id
            ? "/admin/materialManage/batchUpdate"
            : "/admin/materialManage/save",
          {
            ...this.params,
            detail: this.detail.length ? JSON.stringify(this.detail) : "",
            modelConfigJson: this.buildModelConfigJsonPayload(),
            classifyId1: 2,
            status,
            ids: this.id ? [this.id] : "",
          }
        )
        .then(() => {
          this.$message.success("保存成功");
          this.$router.push("/common/list");
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.container-detail-page {
  padding-bottom: 24px;
}

.label {
  font-size: 20px;
  color: #999;
  margin: 15px 0;
}

.container-model-layout {
  display: flex;
  gap: 18px;
  align-items: stretch;
  margin-bottom: 18px;
}

.container-model-card {
  flex: 1.3;
  border-radius: 18px;
}

.container-preview-card {
  flex: 0.9;
  border-radius: 18px;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #2f3a2f;
}

.container-model-form {
  padding-top: 4px;
}

.model-source-switch {
  margin-bottom: 14px;
}

.container-upload-dropzone {
  width: 100%;
}

.dropzone-empty,
.dropzone-filled {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 6px;
}

.dropzone-empty {
  flex-direction: column;
  justify-content: center;
  min-height: 158px;
}

.upload-icon,
.file-icon {
  font-size: 36px;
  color: #7ca176;
}

.upload-title,
.file-name {
  font-size: 16px;
  font-weight: 600;
  color: #314131;
}

.upload-desc,
.file-desc,
.model-url-tip,
.compression-config__desc,
.appearance-panel__text,
.preview-note,
.model-assets-tip {
  font-size: 13px;
  line-height: 1.7;
  color: #7b837b;
}

.file-meta {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.file-desc {
  display: flex;
  gap: 14px;
  margin-top: 6px;
  flex-wrap: wrap;
}

.model-url-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.file-actions .el-button {
  width: auto;
  padding: 0;
}

.danger-text {
  color: #dd6161;
}

.upload-progress {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  color: #409eff;
}

.upload-progress-bar {
  margin-top: 10px;
}

.uploaded-path {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f7faf6;
}

.uploaded-path__label {
  font-size: 12px;
  color: #7f8a7f;
}

.uploaded-path__value {
  font-size: 13px;
  color: #354235;
  word-break: break-all;
}

.compression-config {
  margin-top: 14px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid #edf1ea;
  background: linear-gradient(180deg, #fbfcfa 0%, #f4f8f1 100%);
}

.compression-config__label {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #405040;
}

.compression-dialog-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.compression-dialog-tip {
  font-size: 14px;
  color: #4c5a4c;
  line-height: 1.7;
}

.compression-dialog-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.compression-dialog-option {
  margin-right: 0;
  width: 100%;
  height: auto;
  padding: 12px 14px;
}

.compression-dialog-option ::v-deep .el-radio__label {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  white-space: normal;
}

.compression-dialog-option__title {
  font-size: 14px;
  font-weight: 600;
  color: #213021;
}

.compression-dialog-option__desc {
  font-size: 12px;
  line-height: 1.6;
  color: #6e7c6e;
}

.appearance-panel {
  padding: 14px;
  border-radius: 14px;
  background: #f7faf6;
}

.appearance-panel__status {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.appearance-panel__actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 14px;
}

.appearance-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.appearance-size-note {
  margin-top: 12px;
  font-size: 13px;
  line-height: 1.7;
  color: #5c685c;
}

.preview-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.preview-thumb {
  height: 220px;
  border-radius: 18px;
  overflow: hidden;
  background: linear-gradient(135deg, #eef5e8 0%, #f9fbf7 100%);
}

.preview-thumb__image,
.preview-thumb__empty {
  width: 100%;
  height: 100%;
}

.preview-thumb__image {
  object-fit: cover;
}

.preview-thumb__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #8e9b8a;
  font-size: 14px;
}

.preview-thumb__empty i {
  font-size: 40px;
}

.preview-box {
  padding: 14px;
  border-radius: 14px;
  background: #f7faf6;
}

.preview-box-title,
.model-assets-title {
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #324132;
}

.preview-value {
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.6;
  color: #4a564a;
  word-break: break-word;
}

.model-assets-section {
  padding: 18px;
  border: 1px solid #edf0eb;
  border-radius: 18px;
  background: #fff;
}

.model-assets-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.review-img {
  height: 150px;
}

.model-item {
  padding: 18px 0;
  border-bottom: 1px dashed #dce6d6;
}

.model-item:last-child {
  border-bottom: none;
}

.page-actions {
  gap: 12px;
  margin-top: 20px;
}

.page-actions .el-button {
  flex: 1;
}

@media (max-width: 1080px) {
  .container-model-layout {
    flex-direction: column;
  }
}
</style>
